import React, { useState, useMemo, useEffect } from 'react'
import * as PivotUtilitiesRaw from 'react-pivottable/Utilities'
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight } from 'lucide-react'

// CJS-interop: get PivotData from react-pivottable/Utilities
const PivotDataClass =
  PivotUtilitiesRaw.PivotData ?? PivotUtilitiesRaw.default?.PivotData

/**
 * Computes rowspan size for a cell in grouped row dimensions.
 * Returns -1 if the cell should not be rendered (covered by a parent cell).
 */
function spanSize(arr, i, j) {
  if (i !== 0) {
    let noDraw = true
    for (let x = 0; x <= j; x++) {
      if (arr[i - 1][x] !== arr[i][x]) {
        noDraw = false
        break
      }
    }
    if (noDraw) return -1
  }
  let len = 0
  while (i + len < arr.length) {
    let stop = false
    for (let x = 0; x <= j; x++) {
      if (arr[i][x] !== arr[i + len][x]) {
        stop = true
        break
      }
    }
    if (stop) break
    len++
  }
  return len
}

function parseTimeDimOrder(val) {
  const s = String(val)
  let m
  if ((m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/))) return parseInt(m[3], 10) * 10000 + parseInt(m[2], 10) * 100 + parseInt(m[1], 10)
  if ((m = s.match(/^(\d{4})$/)))                    return parseInt(m[1], 10) * 10000
  if ((m = s.match(/^(\d{2})\/(\d{4})$/)))           return parseInt(m[2], 10) * 10000 + parseInt(m[1], 10) * 100
  if ((m = s.match(/^Q(\d)\/(\d{4})$/)))             return parseInt(m[2], 10) * 10000 + parseInt(m[1], 10) * 1000
  if ((m = s.match(/^KW(\d{2})\/(\d{4})$/)))         return parseInt(m[2], 10) * 10000 + parseInt(m[1], 10)
  return null
}

function compareValues(a, b) {
  const ta = parseTimeDimOrder(a)
  const tb = parseTimeDimOrder(b)
  if (ta !== null && tb !== null) return ta - tb
  const na = parseFloat(a)
  const nb = parseFloat(b)
  if (!isNaN(na) && !isNaN(nb)) return na - nb
  return String(a).localeCompare(String(b))
}

function compareKeyCombination(a, b) {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const cmp = compareValues(a[i] ?? '', b[i] ?? '')
    if (cmp !== 0) return cmp
  }
  return 0
}

function SortIcon({ active, dir, priority, multiSort }) {
  if (!active) return <ChevronsUpDown className="inline-block ml-1 h-3 w-3 text-muted-foreground/50" />
  const arrow = dir === 'asc'
    ? <ChevronUp className="inline-block h-3 w-3" />
    : <ChevronDown className="inline-block h-3 w-3" />
  return (
    <span className="inline-flex items-center ml-1 gap-0.5">
      {arrow}
      {multiSort && <span className="text-[9px] font-bold leading-none">{priority}</span>}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Star-schema aggregation helpers
// ---------------------------------------------------------------------------

/**
 * Build an index from (rowKey, colKey) → Map<object_type, Set<object_id>>.
 * Iterates flatRows once (O(n_rows × 4 combos)) instead of once per cell.
 *
 * Each row contributes to 4 buckets:
 *   (rowKey, colKey)  — the exact cell
 *   (rowKey, [])      — the row total
 *   ([], colKey)      — the col total
 *   ([], [])          — the grand total
 *
 * valueFilter: { attrName: { excludedVal: true } }  — react-pivottable convention
 */
function buildCellObjectIndex(flatRows, rowAttrs, colAttrs, valueFilter) {
  const index = new Map()

  const addEntry = (rk, ck, otype, oid) => {
    const key = JSON.stringify([rk, ck])
    let m = index.get(key)
    if (!m) { m = new Map(); index.set(key, m) }
    let s = m.get(otype)
    if (!s) { s = new Set(); m.set(otype, s) }
    s.add(oid)
  }

  for (const row of flatRows) {
    // Apply valueFilter (same logic as PivotDataClass)
    let skip = false
    for (const attr in valueFilter) {
      const excl = valueFilter[attr]
      if (excl && excl[row[attr] ?? '']) { skip = true; break }
    }
    if (skip) continue

    const oid = row.object_id
    const otype = row.object_type
    if (oid == null || otype == null) continue

    const rk = rowAttrs.map((a) => row[a] ?? '')
    const ck = colAttrs.map((a) => row[a] ?? '')

    addEntry(rk, ck, otype, oid)
    addEntry(rk, [], otype, oid)
    addEntry([], ck, otype, oid)
    addEntry([], [], otype, oid)
  }

  return index
}

/**
 * Look up raw attribute values for `valCol` from dimension tables,
 * using the precomputed index. O(unique_objects_in_cell).
 */
function lookupStarValues(index, rowKey, colKey, valCol, dimMaps) {
  const cellMap = index.get(JSON.stringify([rowKey, colKey]))
  if (!cellMap) return []

  const values = []
  for (const [otype, oids] of cellMap) {
    const dm = dimMaps[otype]
    if (!dm) continue
    for (const oid of oids) {
      const dimRow = dm.get(oid)
      if (dimRow !== undefined) {
        const v = dimRow[valCol]
        if (v !== null && v !== undefined) values.push(v)
      }
    }
  }
  return values
}

/**
 * Apply an aggregator function to an array of raw values.
 * Mirrors the aggregator names exposed in TracePivotTable.jsx.
 */
function applyAggregator(values, aggregatorName) {
  if (!values || values.length === 0) return null
  const nums = values.filter((v) => v !== null && v !== undefined && !Number.isNaN(Number(v)))
  if (nums.length === 0) return null
  const parsed = nums.map(Number)
  switch (aggregatorName) {
    case 'Sum':
      return parsed.reduce((a, b) => a + b, 0)
    case 'Average':
      return parsed.reduce((a, b) => a + b, 0) / parsed.length
    case 'Min':
      return Math.min(...parsed)
    case 'Max':
      return Math.max(...parsed)
    case 'Median': {
      const sorted = [...parsed].sort((a, b) => a - b)
      const mid = Math.floor(sorted.length / 2)
      return sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid]
    }
    case 'Count':
      return values.length
    case 'Count Unique Values':
      return new Set(values.map(String)).size
    case 'First':
      return parsed[0] ?? null
    case 'Last':
      return parsed[parsed.length - 1] ?? null
    default:
      return parsed.reduce((a, b) => a + b, 0)
  }
}

// Format a numeric value to at most 2 decimal places, dropping trailing zeros.
// e.g. 29.4123 → "29.41", 29.0 → "29", 29.1 → "29.1"
function fmt2(val) {
  if (val === null || val === undefined) return ''
  if (typeof val !== 'number' || !isFinite(val)) return String(val)
  if (Number.isInteger(val)) return String(val)
  return String(parseFloat(val.toFixed(2)))
}

function formatStarValue(val, aggregatorName) {
  if (val === null || val === undefined) return ''
  if (aggregatorName === 'Count' || aggregatorName === 'Count Unique Values') {
    return String(Math.round(val))
  }
  return fmt2(val)
}

// ---------------------------------------------------------------------------

const PAGE_SIZE = 10

function formatDivisionValue(value, format) {
  if (value === null || value === undefined) return 'N/A'
  if (typeof value !== 'number' || !isFinite(value)) return 'N/A'
  const decimals = format?.decimals ?? 2
  if (format?.asPercentage) {
    return `${(value * 100).toFixed(decimals)}%`
  }
  return value.toFixed(decimals)
}

export function SortableTableRenderer(props) {
  const { showControls, onToggleControls, starSchema } = props

  const divisionMode = props.divisionMode === true
  const divisionCells = props.divisionCells || null
  const divisionFormat = props.divisionFormat || null
  const unitMultiplier = props.unitMultiplier ?? 1
  const unitLabel = props.unitLabel ?? null
  const applyUnit = (v) => (v !== null && v !== undefined && unitMultiplier !== 1) ? v * unitMultiplier : v

  const [sortColumns, setSortColumns] = useState([])
  const [page, setPage] = useState(0)

  const pivotData = useMemo(
    () => new PivotDataClass(props),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.data, props.rows, props.cols, props.vals, props.aggregatorName, props.valueFilter],
  )

  const divisionLookup = useMemo(() => {
    if (!divisionMode || !divisionCells) return null
    const cellMap = new Map()
    for (const c of divisionCells.cells || []) {
      cellMap.set(JSON.stringify([c.row_key, c.col_key]), c.value)
    }
    const rowTotalMap = new Map()
    for (const r of divisionCells.row_totals || []) {
      rowTotalMap.set(JSON.stringify(r.row_key), r.value)
    }
    const colTotalMap = new Map()
    for (const c of divisionCells.col_totals || []) {
      colTotalMap.set(JSON.stringify(c.col_key), c.value)
    }
    return {
      cell: (rk, ck) => cellMap.get(JSON.stringify([rk, ck])) ?? null,
      rowTotal: (rk) => rowTotalMap.get(JSON.stringify(rk)) ?? null,
      colTotal: (ck) => colTotalMap.get(JSON.stringify(ck)) ?? null,
      grand: divisionCells.grand_total ?? null,
    }
  }, [divisionMode, divisionCells])

  const unitSuffix = unitLabel ? ` [${unitLabel}]` : ''
  const aggLabel = divisionMode
    ? ((divisionFormat?.label?.trim() || 'Division') + unitSuffix)
    : `${props.aggregatorName ?? 'Sum'} (${(props.vals ?? []).join(', ')})${unitSuffix}`

  const colAttrs = pivotData.props.cols
  const rowAttrs = pivotData.props.rows

  // Detect whether the selected value column is an object attribute
  const valCol = (props.vals ?? [])[0]
  const isObjectAttr = Boolean(
    valCol &&
    starSchema?.column_source?.[valCol] &&
    starSchema.column_source[valCol] !== 'events' &&
    starSchema.column_source[valCol] !== 'bridge',
  )

  // Build per-type dimension Maps once per star schema update: O(total_objects)
  const dimMaps = useMemo(() => {
    if (!starSchema?.dimensions) return {}
    const maps = {}
    for (const [otype, dim] of Object.entries(starSchema.dimensions)) {
      const m = new Map()
      for (const row of (dim.rows ?? [])) {
        m.set(row.object_id, row)
      }
      maps[otype] = m
    }
    return maps
  }, [starSchema])

  // Precompute cell → object index: O(n_rows × 4). Rebuilt only when pivot config or data changes.
  // This avoids O(cells × n_rows) iteration during rendering.
  const cellObjectIndex = useMemo(() => {
    if (!isObjectAttr || !starSchema || !props.data) return null
    return buildCellObjectIndex(props.data, rowAttrs, colAttrs, props.valueFilter ?? {})
  // pivotData captures changes to data, rows, cols, vals, valueFilter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pivotData, isObjectAttr, starSchema])

  // Cache the PivotDataClass format function (computed once per pivot config, not per cell)
  const pivotFormat = useMemo(() => {
    const agg = pivotData.getAggregator([], [])
    return agg.format.bind(agg)
  }, [pivotData])

  // Render a cell value as a formatted string.
  const renderCell = (rk, ck) => {
    if (isObjectAttr && cellObjectIndex) {
      const values = lookupStarValues(cellObjectIndex, rk, ck, valCol, dimMaps)
      const val = applyAggregator(values, props.aggregatorName)
      return val !== null ? formatStarValue(val, props.aggregatorName) : ''
    }
    const agg = pivotData.getAggregator(rk, ck)
    const val = agg.value()
    return val !== null ? fmt2(val) : ''
  }

  // Raw numeric value for sort comparisons
  const getCellValue = (rk, ck) => {
    if (isObjectAttr && cellObjectIndex) {
      const values = lookupStarValues(cellObjectIndex, rk, ck, valCol, dimMaps)
      return applyAggregator(values, props.aggregatorName)
    }
    return pivotData.getAggregator(rk, ck).value()
  }

  const rowKeys = pivotData.getRowKeys()
  const colKeys = useMemo(
    () => pivotData.getColKeys()
      .filter(ck => ck.some(k => k != null && k !== ''))
      .sort(compareKeyCombination),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pivotData],
  )
  const showRowTotals = colAttrs.length > 0
  const showAggCol = divisionMode || (props.vals ?? []).length > 0

  const resolveCell = (rowKey, colKey) => {
    if (divisionMode) {
      const raw = divisionLookup ? divisionLookup.cell(rowKey, colKey) : null
      const v = applyUnit(raw)
      return { value: v, formatted: formatDivisionValue(v, divisionFormat) }
    }
    if (isObjectAttr && cellObjectIndex) {
      const v = applyUnit(getCellValue(rowKey, colKey))
      return {
        value: v,
        formatted: showAggCol && v !== null ? formatStarValue(v, props.aggregatorName) : '',
      }
    }
    const agg = pivotData.getAggregator(rowKey, colKey)
    const v = applyUnit(agg.value())
    const formatted = showAggCol && v !== null ? fmt2(v) : ''
    return { value: v, formatted }
  }

  const resolveRowTotal = (rowKey) => {
    if (divisionMode) {
      const v = applyUnit(divisionLookup ? divisionLookup.rowTotal(rowKey) : null)
      return { value: v, formatted: formatDivisionValue(v, divisionFormat) }
    }
    if (isObjectAttr && cellObjectIndex) {
      const v = applyUnit(getCellValue(rowKey, []))
      return { value: v, formatted: v !== null ? formatStarValue(v, props.aggregatorName) : '' }
    }
    const agg = pivotData.getAggregator(rowKey, [])
    const v = applyUnit(agg.value())
    return { value: v, formatted: v !== null ? fmt2(v) : '' }
  }

  const resolveColTotal = (colKey) => {
    if (divisionMode) {
      const v = applyUnit(divisionLookup ? divisionLookup.colTotal(colKey) : null)
      return { value: v, formatted: formatDivisionValue(v, divisionFormat) }
    }
    if (isObjectAttr && cellObjectIndex) {
      const v = applyUnit(getCellValue([], colKey))
      return { value: v, formatted: v !== null ? formatStarValue(v, props.aggregatorName) : '' }
    }
    const agg = pivotData.getAggregator([], colKey)
    const v = applyUnit(agg.value())
    return { value: v, formatted: v !== null ? fmt2(v) : '' }
  }

  const resolveGrandTotal = () => {
    if (divisionMode) {
      const v = applyUnit(divisionLookup ? divisionLookup.grand : null)
      return { value: v, formatted: formatDivisionValue(v, divisionFormat) }
    }
    if (isObjectAttr && cellObjectIndex) {
      const v = applyUnit(getCellValue([], []))
      return { value: v, formatted: v !== null ? formatStarValue(v, props.aggregatorName) : '' }
    }
    const agg = pivotData.getAggregator([], [])
    const v = applyUnit(agg.value())
    return { value: v, formatted: v !== null ? fmt2(v) : '' }
  }

  const sortedRowKeys = useMemo(() => {
    if (sortColumns.length === 0) return [...rowKeys].sort(compareKeyCombination)

    return [...rowKeys].sort((a, b) => {
      for (const { target, dir } of sortColumns) {
        const d = dir === 'asc' ? 1 : -1
        let cmp = 0

        if (target.type === 'rowdim') {
          cmp = d * compareValues(a[target.dimIdx] ?? '', b[target.dimIdx] ?? '')
        } else {
          const ra = target.type === 'total'
            ? resolveRowTotal(a).value
            : resolveCell(a, colKeys[target.colIdx]).value
          const rb = target.type === 'total'
            ? resolveRowTotal(b).value
            : resolveCell(b, colKeys[target.colIdx]).value

          if (ra === null && rb === null) {
            cmp = 0
          } else if (ra === null) {
            cmp = 1
          } else if (rb === null) {
            cmp = -1
          } else if (typeof ra === 'number' && typeof rb === 'number') {
            cmp = d * (ra - rb)
          } else {
            cmp = d * String(ra).localeCompare(String(rb))
          }
        }

        if (cmp !== 0) return cmp
      }
      return 0
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowKeys, colKeys, sortColumns, pivotData, divisionLookup, divisionMode, cellObjectIndex, dimMaps, valCol])

  useEffect(() => { setPage(0) }, [sortColumns, pivotData])

  const visibleRowKeys = sortedRowKeys.filter((rk) => rk.length > 0 && rk.some(k => k != null && k !== ''))
  const totalPages = Math.max(1, Math.ceil(visibleRowKeys.length / PAGE_SIZE))
  const pagedRowKeys = visibleRowKeys.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  const handleColClick = (target) => {
    const key = JSON.stringify(target)
    const defaultDir = target.type === 'rowdim' ? 'asc' : 'desc'
    const oppositeDir = defaultDir === 'asc' ? 'desc' : 'asc'
    setSortColumns((prev) => {
      const existingIdx = prev.findIndex((s) => JSON.stringify(s.target) === key)
      if (existingIdx === -1) {
        return [...prev, { target, dir: defaultDir }]
      }
      if (prev[existingIdx].dir === oppositeDir) {
        return prev.filter((_, i) => i !== existingIdx)
      }
      return prev.map((s, i) =>
        i === existingIdx ? { ...s, dir: oppositeDir } : s
      )
    })
  }

  const isActive = (target) => {
    const key = JSON.stringify(target)
    return sortColumns.some((s) => JSON.stringify(s.target) === key)
  }

  const getSortDir = (target) => {
    const key = JSON.stringify(target)
    return sortColumns.find((s) => JSON.stringify(s.target) === key)?.dir ?? 'asc'
  }

  const getSortPriority = (target) => {
    const key = JSON.stringify(target)
    const idx = sortColumns.findIndex((s) => JSON.stringify(s.target) === key)
    return idx === -1 ? null : idx + 1
  }

  const grouped =
    sortColumns.length === 0 || sortColumns[0].target.type === 'rowdim'

  const grandTotal = resolveGrandTotal()

  const headerCellBase =
    'px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted cursor-pointer select-none hover:bg-accent transition-colors whitespace-nowrap'

  return (
    <div className="rounded-md border border-border">
      {onToggleControls && (
        <div className="flex items-center justify-end px-3 py-1 border-b border-border bg-muted/20">
          <button
            onClick={onToggleControls}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            title={showControls ? 'Hide pivot controls' : 'Show pivot controls'}
          >
            {showControls ? (
              <>Hide controls <ChevronUp className="h-3 w-3" /></>
            ) : (
              <>Show controls <ChevronDown className="h-3 w-3" /></>
            )}
          </button>
        </div>
      )}
      <table className="min-w-full divide-y divide-border text-sm">
        <thead className="bg-muted">
          {colAttrs.length > 0 && (
            <tr>
              {rowAttrs.length > 0 && (
                <th
                  colSpan={rowAttrs.length}
                  className="bg-muted border-b border-border"
                />
              )}
              <th
                colSpan={colKeys.length}
                className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted border-b border-border"
              >
                {colAttrs.join(' / ')}
              </th>
              {showAggCol && (
                <th
                  rowSpan={2}
                  className={headerCellBase.replace('text-left', 'text-right')}
                  onClick={() => handleColClick({ type: 'total' })}
                >
                  {aggLabel}
                  <SortIcon active={isActive({ type: 'total' })} dir={getSortDir({ type: 'total' })} priority={getSortPriority({ type: 'total' })} multiSort={sortColumns.length > 1} />
                </th>
              )}
            </tr>
          )}

          <tr>
            {rowAttrs.map((attr, dimIdx) => (
              <th
                key={`rowAttr-${dimIdx}`}
                className={headerCellBase}
                onClick={() => handleColClick({ type: 'rowdim', dimIdx })}
              >
                {attr}
                <SortIcon active={isActive({ type: 'rowdim', dimIdx })} dir={getSortDir({ type: 'rowdim', dimIdx })} priority={getSortPriority({ type: 'rowdim', dimIdx })} multiSort={sortColumns.length > 1} />
              </th>
            ))}

            {colKeys.map((colKey, colIdx) => (
              <th
                key={`colKey-${colIdx}`}
                className={headerCellBase.replace('text-left', 'text-right')}
                onClick={() => handleColClick({ type: 'col', colIdx })}
              >
                {colKey.map(k => (k == null || k === '') ? '—' : k).join(' / ')}
                <SortIcon active={isActive({ type: 'col', colIdx })} dir={getSortDir({ type: 'col', colIdx })} priority={getSortPriority({ type: 'col', colIdx })} multiSort={sortColumns.length > 1} />
              </th>
            ))}

            {showAggCol && !showRowTotals && (
              <th
                className={headerCellBase.replace('text-left', 'text-right')}
                onClick={() => handleColClick({ type: 'total' })}
              >
                {aggLabel}
                <SortIcon active={isActive({ type: 'total' })} dir={getSortDir({ type: 'total' })} priority={getSortPriority({ type: 'total' })} multiSort={sortColumns.length > 1} />
              </th>
            )}
          </tr>
        </thead>

        <tbody className="divide-y divide-border">
          {pagedRowKeys.map((rowKey, rowIdx) => {
            const rowTotal = resolveRowTotal(rowKey)
            const absoluteIdx = page * PAGE_SIZE + rowIdx
            return (
              <tr key={`row-${absoluteIdx}`} className="hover:bg-muted/40 transition-colors">
                {rowKey.map((val, dimIdx) => {
                  const span = grouped ? spanSize(pagedRowKeys, rowIdx, dimIdx) : 1
                  if (span === -1) return null
                  return (
                    <th
                      key={`rowLabel-${dimIdx}`}
                      className="px-3 py-3 text-left text-sm font-medium text-foreground bg-card whitespace-nowrap"
                      rowSpan={span > 1 ? span : undefined}
                    >
                      {(val == null || val === '') ? '—' : val}
                    </th>
                  )
                })}
                {colKeys.map((colKey, colIdx) => {
                  const cell = resolveCell(rowKey, colKey)
                  return (
                    <td key={`val-${colIdx}`} className="px-3 py-3 text-right tabular-nums text-foreground">
                      {cell.formatted}
                    </td>
                  )
                })}
                {showAggCol && (
                  <td
                    className={
                      showRowTotals
                        ? 'px-3 py-3 text-right tabular-nums font-semibold text-foreground bg-muted/40'
                        : 'px-3 py-3 text-right tabular-nums text-foreground'
                    }
                  >
                    {rowTotal.formatted}
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-muted/20 text-xs text-muted-foreground">
          <span>
            Rows {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, visibleRowKeys.length)} of {visibleRowKeys.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded p-1 hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Previous page"
            >
              <ChevronLeft className="h-3 w-3" />
            </button>
            <span className="px-1">{page + 1} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="rounded p-1 hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Next page"
            >
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
